import { Item, ProductVariant as APIProductVariant, Product as APIProduct } from "@crystallize/schema/catalogue";
import { Product } from "../../contracts/Product";
import { Video } from "../../contracts/Video";
import { ProductVariant } from "../../contracts/ProductVariant";
import {
  chunksForChunkComponentWithId,
  flattenRichText,
  itemsForItemRelationComponentWithId,
  paragraphsForParagraphCollectionComponentWithId,
  sectionsForPropertyTableComponentWithId,
  stringForRichTextComponentWithId,
  stringForSingleLineComponentWithId,
} from "../../mapper/api-mappers";
import { DataMapper } from "..";

function mapVideos(videos: any[] | undefined | null): Video[] {
  if (!videos) return [];
  return videos.map((v: any) => ({
    id: v.id ?? v.key ?? "",
    playlists: v.playlists ?? [],
    playlist: v.playlist ?? v.playlists?.[0] ?? "",
    title: v.title,
    thumbnails: v.thumbnails,
  }));
}

export const fetchProductToProduct = (
  data: Omit<APIProduct, "variants"> &
    Item & { components: any } & {
      variants: Array<
        APIProductVariant & {
          components: any;
          description: any;
          outofstock: {
            content: {
              value: boolean;
            };
          };
        }
      >;
    },
): Product => {
  const mapper = DataMapper();

  const daContent = chunksForChunkComponentWithId(
    data?.components,
    "dameapoteket",
  )?.[0];

  // Fetch story from Dameapoteket chunk if exists
  let story;
  if (process.env.EXTRA_CSS === "da-index.css") {
    story =
      paragraphsForParagraphCollectionComponentWithId(daContent, "story") ||
      paragraphsForParagraphCollectionComponentWithId(
        data?.components,
        "story",
      );
  } else {
    story = paragraphsForParagraphCollectionComponentWithId(
      data?.components,
      "story",
    );
  }

  const sections = sectionsForPropertyTableComponentWithId(
    data?.components,
    "properties",
  );
  const firstDimensionsChunk = chunksForChunkComponentWithId(
    data?.components,
    "dimensions",
  )?.[0];
  const firstSeoChunk = chunksForChunkComponentWithId(
    data?.components,
    "meta",
  )?.[0];
  const downloads = chunksForChunkComponentWithId(
    data?.components,
    "downloads",
  );
  const firstOTCChuck = chunksForChunkComponentWithId(
    data?.components,
    "otc",
  )?.[0];
  const relatedItems = itemsForItemRelationComponentWithId(
    data?.components,
    "related-items",
  );

  // Fetch description from Dameapoteket chunk if exists
  let productDescription;
  if (process.env.EXTRA_CSS === "da-index.css") {
    productDescription =
      stringForRichTextComponentWithId(daContent, "description") ||
      stringForRichTextComponentWithId(data?.components, "description") ||
      data?.name!;
  } else {
    productDescription =
      stringForRichTextComponentWithId(data?.components, "description") ||
      data?.name!;
  }

  const variants: ProductVariant[] =
    data?.variants?.map((variant) => {
      const mappedVariant =
        mapper.API.Object.APIProductVariantToProductVariant(variant);
      let description = productDescription;
      const variantDescription =
        variant?.description?.content?.selectedComponent?.content?.plainText?.join(
          "",
        );
      const variantDescriptionType =
        variant?.description?.content?.selectedComponent?.id;
      if (variantDescriptionType) {
        description =
          variantDescriptionType === "extra"
            ? productDescription + " " + variantDescription
            : variantDescription;
      }
      return {
        ...mappedVariant,
        description,
      };
    }) || [];

  const defaultVariant =
    variants.find((variant) => variant.isDefault) || variants[0];

  const dto: Product = {
    id: data?.id!,
    path: data?.path!,
    name: data?.name!,
    title:
      stringForSingleLineComponentWithId(data?.components, "title") ||
      data?.name!,
    description: productDescription,
    story:
      story?.map((paragraph) => {
        return {
          title: paragraph.title?.text || "",
          body: flattenRichText(paragraph.body),
          images: mapper.API.Object.APIImageToImage(paragraph.images),
          videos: mapVideos(paragraph.videos),
        };
      }) || [],
    specifications:
      sections?.map((section) => {
        return {
          title: section.title || "",
          properties: section.properties || {},
        };
      }) || [],
    dimensions: !firstDimensionsChunk
      ? []
      : firstDimensionsChunk.reduce(
          (
            memo: Record<
              string,
              {
                title: string;
                value: number;
                unit: string;
              }
            >,
            data: any,
          ) => {
            return {
              ...memo,
              [data.id]: {
                title: data.name,
                value: data?.content?.number || 0.0,
                unit: data?.content?.unit || "",
              },
            };
          },
          {},
        ),
    downloads:
      downloads?.map((chunk) => {
        const mapped = chunk.reduce((memo: Record<string, any>, data: any) => {
          let value = undefined;
          switch (data.type) {
            case "singleLine":
              value = data.content?.text || "";
              break;
            case "richText":
              value = data.content?.json || "";
              break;
            case "files":
              value =
                data.content?.files?.map((file: any) => {
                  return {
                    url: file.url,
                    title: file.title || "",
                  };
                }) || [];
              break;
          }
          return {
            ...memo,
            [data.id]: value,
          };
        }, {});

        return {
          title: mapped["title" as keyof typeof mapped] || "",
          description: {
            json: mapped["description" as keyof typeof mapped],
          },
          files: mapped["files" as keyof typeof mapped],
        };
      }) || [],
    relatedItems:
      relatedItems?.map((item) => {
        return {
          id: item.id,
          name: item.name,
          path: item.path,
          sku: item.sku,
          topics: [],
          variant: mapper.API.Object.APIProductVariantToProductVariant(
            item.defaultVariant,
          ),
          brand: item.brand?.content?.text,
        };
      }) || [],
    topics:
      data?.topics?.map((topic) => {
        return {
          name: topic.name!,
          path: topic.path!,
        };
      }) || [],
    seo: mapper.API.Object.APIMetaSEOComponentToSEO(firstSeoChunk),
    vat: {
      name: data?.vatType?.name || "Exempt.",
      rate: data?.vatType?.percent || 0.0,
    },
    variants,
    defaultVariant,
    ean: stringForSingleLineComponentWithId(data?.components, "ean"),
    otc: mapper.API.Object.APIOTCComponentToOTC(firstOTCChuck),
    brand: stringForSingleLineComponentWithId(data?.components, "brand"),
    mpn: stringForSingleLineComponentWithId(data?.components, "mpn"),
  };
  return dto;
};
